/**
 * The first-party OAuth resume path lives in localStorage across the provider
 * round trip (the provider-facing callback only carries a short `next`).
 *
 * AuthContext consumes it on return and `window.location.replace`s to it. That
 * replace is not atomic with the document: a chunk-error reload (lib/chunk-error.ts,
 * the `_r=` cache-buster) fired in the same tick wins the navigation, and a key
 * deleted before the replace committed leaves the reload with nothing to
 * recover. Two of nine refinement-gate sign-ins in launch week ended that way,
 * returning to a bare ZIP step with the photo and lineage gone.
 *
 * So the key survives until the destination consumes it (the analysis hook
 * clears it when its URL restore applies), bounded by an attempt cap so a
 * destination that never consumes cannot loop.
 */
export const OAUTH_PENDING_REDIRECT_KEY = 'oauth_pending_redirect'
export const OAUTH_PENDING_REDIRECT_TTL_MS = 15 * 60 * 1000 // 15 minutes
/** Replaces attempted from stored copies of one path before it is dropped. */
export const OAUTH_PENDING_REDIRECT_MAX_ATTEMPTS = 2

export type PendingOAuthRedirect = { path: string; savedAt: number; attempts?: number }

/**
 * True once this document has committed to a `window.location.replace` for the
 * pending OAuth path. The replace tears the document down asynchronously, so
 * work scheduled after it (an auto-run analyze, its analytics) still executes
 * against a page that is already leaving. localStorage cannot carry this: the
 * pending key is consumed before the replace, so a key check reads false for
 * every caller that needs the answer. Module scope is the correct lifetime -
 * the flag must die with the document.
 */
let oauthRedirectReplacing = false

export function markOAuthRedirectReplacing(): void {
  oauthRedirectReplacing = true
}

export function isOAuthRedirectReplacing(): boolean {
  return oauthRedirectReplacing
}

/** Test-only reset; a real document never un-commits a replace. */
export function resetOAuthRedirectReplacingForTests(): void {
  oauthRedirectReplacing = false
}

export function clearPendingOAuthRedirect(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(OAUTH_PENDING_REDIRECT_KEY)
  } catch {
    // localStorage may be blocked
  }
}
